import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DollarSign, TrendingUp, TrendingDown, ArrowLeft, RefreshCw, Calendar, Filter } from 'lucide-react'
import { api } from '../../../lib/api'
import { useAuth } from '../../../contexts/AuthContext'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache } from '../../../lib/cache'
import { showOfflineToast } from '../../../lib/toastManager'

interface Transaction {
  id: string
  type: 'income' | 'expense'
  amount: number
  description: string
  order_id?: string
  created_at: string
  payment_method?: string
}

interface CashSummary {
  total_income: number
  total_expenses: number
  current_balance: number
  today_income: number
  today_expenses: number
  pending_transfers: number
}

const StoreCashRegister = () => {
  const navigate = useNavigate()
  const params = useParams()
  const { user } = useAuth()
  const storeId = params.storeId || user?.storeId

  const [summary, setSummary] = useState<CashSummary | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month' | 'all'>('today')

  useEffect(() => {
    if (storeId) {
      loadData()
    }
  }, [storeId, dateFilter])

  const loadData = async () => {
    if (!storeId) return

    try {
      setRefreshing(true)
      await Promise.all([
        loadSummary(),
        loadTransactions()
      ])
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const loadSummary = async () => {
    if (!storeId) return

    const cacheKey = `store_cash_summary_${storeId}`
    
    // Intentar cargar desde cache primero
    const cachedSummary = getCacheStale<CashSummary>(cacheKey)
    if (cachedSummary) {
      setSummary(cachedSummary)
    }

    try {
      // Por ahora, calcular desde las órdenes
      // TODO: Implementar endpoint específico en backend
      const response = await api.get(`/orders/store/${storeId}?limit=1000`)
      if (response.data.success) {
        const orders = response.data.data || []
        
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        
        const todayOrders = orders.filter((o: any) => {
          const orderDate = new Date(o.created_at)
          return orderDate >= today && o.status === 'delivered'
        })

        const totalIncome = orders
          .filter((o: any) => o.status === 'delivered')
          .reduce((sum: number, o: any) => sum + (Number(o.total_amount) + Number(o.delivery_fee || 0)), 0)

        const todayIncome = todayOrders.reduce((sum: number, o: any) => 
          sum + (Number(o.total_amount) + Number(o.delivery_fee || 0)), 0)

        // Obtener entregas de dinero de delivery
        const deliveryDeliveries = orders
          .filter((o: any) => o.status === 'delivered' && o.payment_method === 'cash')
          .reduce((sum: number, o: any) => sum + (Number(o.total_amount) + Number(o.delivery_fee || 0)), 0)

        const summaryData: CashSummary = {
          total_income: totalIncome,
          total_expenses: 0, // TODO: Implementar cuando haya sistema de gastos
          current_balance: totalIncome - deliveryDeliveries, // Balance aproximado
          today_income: todayIncome,
          today_expenses: 0,
          pending_transfers: 0 // TODO: Implementar cuando haya transferencias pendientes
        }
        
        setSummary(summaryData)
        
        // Guardar en cache (2 minutos de TTL - datos financieros cambian frecuentemente)
        setCache(cacheKey, summaryData, 2 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading summary:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedSummary) {
        showOfflineToast() // Usar gestor centralizado (no muestra toast, solo log)
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar resumen de caja')
    }
  }

  const loadTransactions = async () => {
    if (!storeId) return

    const cacheKey = `store_cash_transactions_${storeId}_${dateFilter}`
    
    // Intentar cargar desde cache primero
    const cachedTransactions = getCacheStale<Transaction[]>(cacheKey)
    if (cachedTransactions && cachedTransactions.length > 0) {
      setTransactions(cachedTransactions)
    }

    try {
      const response = await api.get(`/orders/store/${storeId}?limit=100`)
      if (response.data.success) {
        const orders = response.data.data || []
        
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const weekAgo = new Date(today)
        weekAgo.setDate(weekAgo.getDate() - 7)
        const monthAgo = new Date(today)
        monthAgo.setMonth(monthAgo.getMonth() - 1)

        let filteredOrders = orders.filter((o: any) => o.status === 'delivered')
        
        if (dateFilter === 'today') {
          filteredOrders = filteredOrders.filter((o: any) => {
            const orderDate = new Date(o.created_at)
            return orderDate >= today
          })
        } else if (dateFilter === 'week') {
          filteredOrders = filteredOrders.filter((o: any) => {
            const orderDate = new Date(o.created_at)
            return orderDate >= weekAgo
          })
        } else if (dateFilter === 'month') {
          filteredOrders = filteredOrders.filter((o: any) => {
            const orderDate = new Date(o.created_at)
            return orderDate >= monthAgo
          })
        }

        const transactionsList: Transaction[] = filteredOrders.map((order: any) => ({
          id: order.id,
          type: 'income' as const,
          amount: Number(order.total_amount) + Number(order.delivery_fee || 0),
          description: `Pedido #${order.id.substring(0, 8)} - ${order.customer_name}`,
          order_id: order.id,
          created_at: order.created_at,
          payment_method: order.payment_method
        }))
        
        setTransactions(transactionsList)
        
        // Guardar en cache (2 minutos de TTL)
        setCache(cacheKey, transactionsList, 2 * 60 * 1000)

        setTransactions(transactionsList.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        ))
      }
    } catch (error: any) {
      console.error('Error loading transactions:', error)
      toast.error('Error al cargar transacciones')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-600" />
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-16 pb-20">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-4 mb-4 sm:mb-6">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/70 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-light text-gray-900 tracking-tight">Caja del Negocio</h1>
              <p className="text-sm sm:text-base text-gray-500 mt-1">Balance y transacciones financieras</p>
            </div>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-gray-500" />
            <div className="flex gap-2 bg-white/70 backdrop-blur-xl rounded-xl p-1 border border-gray-200/50">
              {(['today', 'week', 'month', 'all'] as const).map((filter) => (
                <button
                  key={filter}
                  onClick={() => setDateFilter(filter)}
                  className={`px-3 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                    dateFilter === filter
                      ? 'bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100/50'
                  }`}
                >
                  {filter === 'today' ? 'Hoy' : filter === 'week' ? 'Semana' : filter === 'month' ? 'Mes' : 'Todo'}
                </button>
              ))}
            </div>
            <button
              onClick={loadData}
              disabled={refreshing}
              className="ml-auto flex items-center gap-2 px-3 sm:px-4 py-2 bg-white/80 backdrop-blur-xl text-gray-700 rounded-xl font-medium shadow-lg shadow-black/5 border border-gray-200/50 hover:bg-white hover:shadow-xl transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Actualizar</span>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-6 sm:mb-8">
            <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Balance Actual</p>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                  <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              <p className="text-2xl sm:text-3xl font-light text-emerald-600">RD${summary.current_balance.toFixed(2)}</p>
            </div>

            <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Ingresos Hoy</p>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              <p className="text-2xl sm:text-3xl font-light text-blue-600">RD${summary.today_income.toFixed(2)}</p>
            </div>

            <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Ingresos Totales</p>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-400 to-purple-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/20">
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              <p className="text-2xl sm:text-3xl font-light text-purple-600">RD${summary.total_income.toFixed(2)}</p>
            </div>

            <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Transferencias Pendientes</p>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/20">
                  <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              <p className="text-2xl sm:text-3xl font-light text-orange-600">RD${summary.pending_transfers.toFixed(2)}</p>
            </div>
          </div>
        )}

        {/* Transactions */}
        <div className="bg-white/70 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-xl shadow-black/5 border border-white/60 p-4 sm:p-6 md:p-8">
          <h2 className="text-lg sm:text-xl font-light mb-4 sm:mb-6 text-gray-900">Transacciones Recientes</h2>
          
          {transactions.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <p className="text-gray-400 text-sm sm:text-base">No hay transacciones en el período seleccionado</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-3 sm:p-4 bg-white/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-gray-100/50 hover:bg-white/70 transition-all"
                >
                  <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-md flex-shrink-0 ${
                      transaction.type === 'income'
                        ? 'bg-gradient-to-br from-emerald-400 to-emerald-500 shadow-emerald-500/20'
                        : 'bg-gradient-to-br from-red-400 to-red-500 shadow-red-500/20'
                    }`}>
                      {transaction.type === 'income' ? (
                        <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      ) : (
                        <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{transaction.description}</p>
                      <p className="text-xs sm:text-sm text-gray-500">
                        {new Date(transaction.created_at).toLocaleDateString('es-DO', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {transaction.payment_method && ` • ${transaction.payment_method === 'cash' ? 'Efectivo' : transaction.payment_method === 'card' ? 'Tarjeta' : transaction.payment_method}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className={`text-base sm:text-lg font-light ${
                      transaction.type === 'income' ? 'text-emerald-600' : 'text-red-600'
                    }`}>
                      {transaction.type === 'income' ? '+' : '-'}RD${transaction.amount.toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default StoreCashRegister

